import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Search, ToggleLeft, ToggleRight, X, Tag, ChevronDown, ChevronUp, Upload } from 'lucide-react';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { wholesalerAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import useRangeSelect from '../../hooks/useRangeSelect';
import DragSelectOverlay from '../../components/common/DragSelectOverlay';
import FloatingActionBar from '../../components/common/FloatingActionBar';
import ConfirmModal from '../../components/common/ConfirmModal';

// ================================================================
// MODAL: Create / Edit Product Discount
// Create mode: select a business, set %, pick multiple products
// Edit mode: single product, can change %, reason, expiry
// ================================================================
const ProductDiscountModal = ({ discount, onClose, onSave, onBulkSave }) => {
  const { t } = useTranslation('wholesaler');
  const isEdit = !!discount;
  const [formData, setFormData] = useState({
    businessId: discount?.businessId?._id || '',
    percentage: discount?.percentage || '',
    reason: discount?.reason || '',
    expiresAt: discount?.expiresAt ? new Date(discount.expiresAt).toISOString().split('T')[0] : '',
  });
  const [selectedProductIds, setSelectedProductIds] = useState(
    isEdit ? new Set([discount.productId?._id]) : new Set()
  );
  const [businesses, setBusinesses] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Load businesses: approved (catalog access) + past customers, merged & deduplicated
  useEffect(() => {
    const loadBusinesses = async () => {
      try {
        const seen = new Map();
        try {
          const catRes = await wholesalerAPI.getCatalogRequests();
          const approved = catRes.data.approvedBusinesses || [];
          for (const ab of approved) {
            const id = ab.businessId?._id || ab.businessId;
            if (id) seen.set(id.toString(), {
              _id: id,
              businessName: ab.businessName || ab.businessId?.businessName || '',
              vatNumber: ab.vatNumber || ab.businessId?.vatNumber || ''
            });
          }
        } catch (e) { /* catalog requests may not be available */ }
        try {
          const custRes = await wholesalerAPI.getCustomers();
          const customers = custRes.data.customers || custRes.data || [];
          for (const c of customers) {
            const id = (c._id || '').toString();
            if (id && !seen.has(id)) {
              seen.set(id, { _id: id, businessName: c.businessName || '', vatNumber: c.vatNumber || '' });
            }
          }
        } catch (e) { /* customers endpoint may fail */ }
        setBusinesses(Array.from(seen.values()));
      } catch (err) {
        console.error('Error loading businesses:', err);
      }
    };
    loadBusinesses();
  }, []);

  // Load products
  useEffect(() => {
    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const res = await wholesalerAPI.getProducts({ limit: 500 });
        setProducts(res.data.products || res.data || []);
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  const filteredProducts = products.filter(p => {
    if (!productSearch) return true;
    const s = productSearch.toLowerCase();
    return p.name?.toLowerCase().includes(s) || p.sku?.toLowerCase().includes(s);
  });

  const toggleProduct = (productId) => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      filteredProducts.slice(0, 100).forEach(p => next.add(p._id));
      return next;
    });
  };

  const deselectAll = () => setSelectedProductIds(new Set());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.businessId) {
      setError(t('productDiscounts.errors.selectBusiness'));
      return;
    }
    if (selectedProductIds.size === 0) {
      setError(t('productDiscounts.errors.selectProduct'));
      return;
    }
    if (!formData.percentage || formData.percentage < 0 || formData.percentage > 100) {
      setError(t('productDiscounts.errors.invalidPercentage'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const pct = parseFloat(formData.percentage);
      const expires = formData.expiresAt || null;

      if (isEdit) {
        // Edit mode — single product update
        await onSave({
          ...formData,
          productId: [...selectedProductIds][0],
          percentage: pct,
          expiresAt: expires
        });
      } else {
        // Create mode — bulk create for all selected products
        const discounts = [...selectedProductIds].map(productId => ({
          businessId: formData.businessId,
          productId,
          percentage: pct,
          reason: formData.reason,
          expiresAt: expires
        }));
        await onBulkSave(discounts);
      }
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('productDiscounts.errors.saveFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold">
            {isEdit ? t('productDiscounts.editDiscount') : t('productDiscounts.createDiscount')}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          {/* Business selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productDiscounts.business')} *
            </label>
            <select
              value={formData.businessId}
              onChange={(e) => setFormData({ ...formData, businessId: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={isEdit}
            >
              <option value="">{t('productDiscounts.selectBusiness')}</option>
              {businesses.map(b => (
                <option key={b._id} value={b._id}>
                  {b.businessName} {b.vatNumber ? `(${b.vatNumber})` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productDiscounts.percentage')} *
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={formData.percentage}
                onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0-100"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productDiscounts.reason')}
            </label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('productDiscounts.reasonPlaceholder')}
            />
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('productDiscounts.expirationDate')}
            </label>
            <input
              type="date"
              value={formData.expiresAt}
              onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Product selector — multi-select with checkboxes (create) or single display (edit) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isEdit ? t('productDiscounts.product') : t('productDiscounts.selectProducts', 'Products')} *
              {!isEdit && selectedProductIds.size > 0 && (
                <span className="ml-2 text-blue-600 font-normal">({selectedProductIds.size} {t('productDiscounts.selected', 'selected')})</span>
              )}
            </label>

            {isEdit ? (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
                {discount.productId?.name || '-'} {discount.productId?.sku ? `[${discount.productId.sku}]` : ''}
              </div>
            ) : (
              <>
                <input
                  type="text"
                  placeholder={t('productDiscounts.searchProducts')}
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="flex items-center gap-2 mb-2">
                  <button type="button" onClick={selectAllVisible} className="text-xs text-blue-600 hover:underline">
                    {t('productDiscounts.selectAllVisible', 'Select all visible')}
                  </button>
                  <span className="text-gray-300">|</span>
                  <button type="button" onClick={deselectAll} className="text-xs text-gray-500 hover:underline">
                    {t('productDiscounts.deselectAll', 'Deselect all')}
                  </button>
                </div>
                <div className="border border-gray-300 rounded-lg max-h-56 overflow-y-auto">
                  {loadingProducts ? (
                    <div className="p-3 text-sm text-gray-500 text-center">Loading...</div>
                  ) : filteredProducts.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">{t('productDiscounts.noProductsFound', 'No products found')}</div>
                  ) : (
                    filteredProducts.slice(0, 100).map(p => (
                      <label
                        key={p._id}
                        className={`flex items-center gap-3 px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0 ${
                          selectedProductIds.has(p._id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProductIds.has(p._id)}
                          onChange={() => toggleProduct(p._id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-900 flex-1 truncate">
                          {p.name} {p.sku ? `[${p.sku}]` : ''}
                        </span>
                        {p.pricing?.unitPrice != null && (
                          <span className="text-xs text-gray-500 flex-shrink-0">€{p.pricing.unitPrice}</span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {t('cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading
                ? t('productDiscounts.saving')
                : isEdit
                  ? t('productDiscounts.save')
                  : selectedProductIds.size > 1
                    ? t('productDiscounts.createCount', { count: selectedProductIds.size }) || `Create ${selectedProductIds.size} Discounts`
                    : t('productDiscounts.create')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ================================================================
// MAIN PAGE
// ================================================================
const ProductDiscounts = () => {
  const { t } = useTranslation('wholesaler');
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBusiness, setFilterBusiness] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [usage, setUsage] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const fetchDiscounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 200 };
      if (filterBusiness) params.businessId = filterBusiness;
      if (filterStatus === 'active') params.active = 'true';
      if (filterStatus === 'inactive') params.active = 'false';

      const res = await wholesalerAPI.getProductDiscounts(params);
      setDiscounts(res.data.discounts || []);
      setUsage(res.data.usage || null);
      setError(null);
    } catch (err) {
      setError(t('productDiscounts.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filterBusiness, filterStatus, t]);

  useEffect(() => { fetchDiscounts(); }, [fetchDiscounts]);

  // Load businesses for filter dropdown (approved + past customers)
  useEffect(() => {
    const loadBusinesses = async () => {
      try {
        const seen = new Map();
        try {
          const catRes = await wholesalerAPI.getCatalogRequests();
          for (const ab of (catRes.data.approvedBusinesses || [])) {
            const id = ab.businessId?._id || ab.businessId;
            if (id) seen.set(id.toString(), {
              _id: id,
              businessName: ab.businessName || ab.businessId?.businessName || '',
              vatNumber: ab.vatNumber || ''
            });
          }
        } catch (e) { /* ignore */ }
        try {
          const custRes = await wholesalerAPI.getCustomers();
          for (const c of (custRes.data.customers || custRes.data || [])) {
            const id = (c._id || '').toString();
            if (id && !seen.has(id)) {
              seen.set(id, { _id: id, businessName: c.businessName || '', vatNumber: c.vatNumber || '' });
            }
          }
        } catch (e) { /* ignore */ }
        setBusinesses(Array.from(seen.values()));
      } catch (err) {
        console.error('Error loading businesses:', err);
      }
    };
    loadBusinesses();
  }, []);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleCreate = async (data) => {
    await wholesalerAPI.createProductDiscount(data);
    showSuccess(t('productDiscounts.createSuccess'));
    fetchDiscounts();
  };

  const handleBulkCreate = async (discounts) => {
    await wholesalerAPI.bulkCreateProductDiscounts({ discounts });
    showSuccess(t('productDiscounts.createSuccess'));
    fetchDiscounts();
  };

  const handleUpdate = async (data) => {
    await wholesalerAPI.updateProductDiscount(editingDiscount._id, data);
    showSuccess(t('productDiscounts.updateSuccess'));
    fetchDiscounts();
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: t('productDiscounts.confirmDeleteTitle', 'Delete Discount'),
      message: t('productDiscounts.confirmDelete'),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          await wholesalerAPI.deleteProductDiscount(id);
          showSuccess(t('productDiscounts.deleteSuccess'));
          fetchDiscounts();
        } catch (err) {
          setError(t('productDiscounts.errors.deleteFailed'));
        }
      }
    });
  };

  const handleToggle = async (id) => {
    try {
      await wholesalerAPI.toggleProductDiscount(id);
      showSuccess(t('productDiscounts.toggleSuccess'));
      fetchDiscounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Toggle failed');
    }
  };

  const handleBulkImport = async () => {
    if (!bulkText.trim()) return;
    setBulkLoading(true);
    setBulkResults(null);
    try {
      const lines = bulkText.trim().split('\n').filter(l => l.trim());
      const items = lines.map(line => {
        const parts = line.split(',').map(s => s.trim());
        return { sku: parts[0], vatNumber: parts[1], percentage: parseFloat(parts[2]), reason: parts[3] || '' };
      });

      // We need to resolve SKU→productId and VAT→businessId
      // For now, send raw data — the bulk endpoint uses IDs, so we need to map
      // This is a simplified version that assumes the user has IDs
      // In a real implementation, we'd add a server-side CSV endpoint
      const res = await wholesalerAPI.bulkCreateProductDiscounts({ discounts: items });
      setBulkResults(res.data.results);
      fetchDiscounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Bulk import failed');
    } finally {
      setBulkLoading(false);
    }
  };

  // Client-side search filtering
  const filtered = discounts.filter(d => {
    if (!searchQuery) return true;
    const s = searchQuery.toLowerCase();
    return (
      d.businessId?.businessName?.toLowerCase().includes(s) ||
      d.productId?.name?.toLowerCase().includes(s) ||
      d.productId?.sku?.toLowerCase().includes(s)
    );
  });

  const {
    selectedIds, handleItemClick, selectAll, clearSelection,
    dragProps, isDragging, dragRect, dragSelectedIds,
  } = useRangeSelect(filtered);

  const handleBulkDelete = () => {
    setConfirmModal({
      isOpen: true,
      title: t('productDiscounts.bulkDeleteTitle', 'Delete Selected'),
      message: t('productDiscounts.bulkDeleteMessage', { count: selectedIds.size }) || `Delete ${selectedIds.size} selected discounts?`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setBulkActionLoading(true);
        try {
          await Promise.all([...selectedIds].map(id => wholesalerAPI.deleteProductDiscount(id)));
          showSuccess(t('productDiscounts.bulkDeleteSuccess', 'Deleted successfully'));
          clearSelection();
          fetchDiscounts();
        } catch (err) {
          setError(t('productDiscounts.errors.bulkDeleteFailed', 'Bulk delete failed'));
        } finally {
          setBulkActionLoading(false);
        }
      }
    });
  };

  const handleBulkToggle = async () => {
    setBulkActionLoading(true);
    try {
      await Promise.all([...selectedIds].map(id => wholesalerAPI.toggleProductDiscount(id)));
      showSuccess(t('productDiscounts.bulkToggleSuccess', 'Toggled successfully'));
      clearSelection();
      fetchDiscounts();
    } catch (err) {
      setError(t('productDiscounts.errors.bulkToggleFailed', 'Bulk toggle failed'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Summary stats
  const activeDiscounts = discounts.filter(d => d.active);
  const avgPct = activeDiscounts.length > 0
    ? (activeDiscounts.reduce((sum, d) => sum + d.percentage, 0) / activeDiscounts.length).toFixed(1)
    : 0;
  const uniqueProducts = new Set(activeDiscounts.map(d => d.productId?._id)).size;

  const getStatusBadge = (d) => {
    if (d.expiresAt && new Date(d.expiresAt) < new Date()) {
      return <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">{t('productDiscounts.expired')}</span>;
    }
    return d.active
      ? <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">{t('productDiscounts.active')}</span>
      : <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded-full">{t('productDiscounts.inactive')}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-14 lg:mt-0">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Tag className="w-6 h-6 text-blue-600" />
              {t('productDiscounts.title')}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t('productDiscounts.subtitle')}</p>
            {usage && (
              <p className="text-xs text-gray-400 mt-1">
                {usage.limit === -1
                  ? t('productDiscounts.usageUnlimited', { active: usage.active })
                  : t('productDiscounts.usageInfo', { active: usage.active, limit: usage.limit })
                }
              </p>
            )}
          </div>
          <button
            onClick={() => { setEditingDiscount(null); setShowModal(true); }}
            disabled={usage && !usage.canCreate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            {t('productDiscounts.addDiscount')}
          </button>
        </div>

        {/* Success / Error messages */}
        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{successMsg}</div>
        )}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex justify-between">
            {error}
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">{t('productDiscounts.totalActive')}</p>
            <p className="text-2xl font-bold text-blue-600">{activeDiscounts.length}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">{t('productDiscounts.avgDiscount')}</p>
            <p className="text-2xl font-bold text-green-600">{avgPct}%</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">{t('productDiscounts.topProducts')}</p>
            <p className="text-2xl font-bold text-purple-600">{uniqueProducts}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t('productDiscounts.searchBusinessOrProduct')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
            <select
              value={filterBusiness}
              onChange={(e) => setFilterBusiness(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('productDiscounts.allBusinesses')}</option>
              {businesses.map(b => (
                <option key={b._id} value={b._id}>{b.businessName}</option>
              ))}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('productDiscounts.allStatuses')}</option>
              <option value="active">{t('productDiscounts.active')}</option>
              <option value="inactive">{t('productDiscounts.inactive')}</option>
            </select>
          </div>
        </div>

        {/* Bulk Import (collapsible) */}
        <div className="bg-white rounded-lg border mb-6">
          <button
            onClick={() => setBulkOpen(!bulkOpen)}
            className="w-full flex items-center justify-between p-4 text-left"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Upload className="w-4 h-4" />
              {t('productDiscounts.bulkImport')}
            </span>
            {bulkOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {bulkOpen && (
            <div className="px-4 pb-4 border-t">
              <p className="text-xs text-gray-500 mt-3 mb-2">{t('productDiscounts.bulkImportDesc')}</p>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={t('productDiscounts.bulkPlaceholder')}
                rows={5}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleBulkImport}
                disabled={bulkLoading || !bulkText.trim()}
                className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {bulkLoading ? '...' : t('productDiscounts.bulkProcess')}
              </button>
              {bulkResults && (
                <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                  <p className="font-medium">{t('productDiscounts.bulkResults')}</p>
                  <p>{t('productDiscounts.created')}: {bulkResults.created}</p>
                  <p>{t('productDiscounts.updated')}: {bulkResults.updated}</p>
                  {bulkResults.errors?.length > 0 && (
                    <p className="text-red-600">{t('productDiscounts.errorsCount')}: {bulkResults.errors.length}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Discount List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg border p-12 text-center">
            <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">{t('productDiscounts.noDiscounts')}</h3>
            <p className="text-sm text-gray-500 mt-1">{t('productDiscounts.noDiscountsDesc')}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden" {...dragProps} style={{ position: 'relative' }}>
            <DragSelectOverlay isDragging={isDragging} dragRect={dragRect} />
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-3 text-center w-10">
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && selectedIds.size === filtered.length}
                        onChange={() => selectedIds.size === filtered.length ? clearSelection() : selectAll()}
                        className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('productDiscounts.business')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('productDiscounts.product')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('productDiscounts.percentage')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('productDiscounts.reason')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('productDiscounts.status')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('productDiscounts.timesUsed')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('productDiscounts.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filtered.map(d => (
                    <tr key={d._id} data-select-id={d._id} className={`hover:bg-gray-50 ${selectedIds.has(d._id) ? 'bg-indigo-50' : dragSelectedIds.has(d._id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(d._id)}
                          readOnly onClick={(e) => { e.stopPropagation(); handleItemClick(d._id, e); }}
                          className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{d.businessId?.businessName || '-'}</div>
                        {d.businessId?.vatNumber && (
                          <div className="text-xs text-gray-400">{d.businessId.vatNumber}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{d.productId?.name || '-'}</div>
                        {d.productId?.sku && (
                          <div className="text-xs text-gray-400">{d.productId.sku}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-semibold text-blue-600">{d.percentage}%</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">
                        {d.reason || '-'}
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(d)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{d.timesUsed || 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleToggle(d._id)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg"
                            title={d.active ? t('common:bulkActions.deactivate') : t('common:bulkActions.activate')}
                          >
                            {d.active
                              ? <ToggleRight className="w-4 h-4 text-green-600" />
                              : <ToggleLeft className="w-4 h-4 text-gray-400" />
                            }
                          </button>
                          <button
                            onClick={() => { setEditingDiscount(d); setShowModal(true); }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg"
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={() => handleDelete(d._id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-200">
              {filtered.map(d => (
                <div key={d._id} data-select-id={d._id} className={`p-4 ${selectedIds.has(d._id) ? 'bg-indigo-50' : dragSelectedIds.has(d._id) ? 'bg-blue-50' : ''}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(d._id)}
                        readOnly onClick={(e) => { e.stopPropagation(); handleItemClick(d._id, e); }}
                        className="w-4 h-4 mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </div>
                    <div className="flex-1 ml-2">
                      <p className="font-medium text-gray-900">{d.productId?.name || '-'}</p>
                      <p className="text-xs text-gray-500">{d.productId?.sku}</p>
                    </div>
                    <span className="text-lg font-bold text-blue-600">{d.percentage}%</span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-gray-600">{d.businessId?.businessName}</span>
                    {getStatusBadge(d)}
                  </div>
                  {d.reason && <p className="text-xs text-gray-500 mb-2">{d.reason}</p>}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {t('productDiscounts.timesUsed')}: {d.timesUsed || 0}
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleToggle(d._id)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                        {d.active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-gray-400" />}
                      </button>
                      <button onClick={() => { setEditingDiscount(d); setShowModal(true); }} className="p-1.5 hover:bg-gray-100 rounded-lg">
                        <Edit2 className="w-4 h-4 text-gray-500" />
                      </button>
                      <button onClick={() => handleDelete(d._id)} className="p-1.5 hover:bg-red-50 rounded-lg">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />

      {/* Modal */}
      {showModal && (
        <ProductDiscountModal
          discount={editingDiscount}
          onClose={() => { setShowModal(false); setEditingDiscount(null); }}
          onSave={editingDiscount ? handleUpdate : handleCreate}
          onBulkSave={handleBulkCreate}
        />
      )}

      <FloatingActionBar count={selectedIds.size} onClear={clearSelection} onSelectAll={selectAll}>
        <button
          onClick={handleBulkToggle}
          disabled={bulkActionLoading}
          className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-lg text-sm font-medium hover:bg-amber-200 disabled:opacity-50"
        >
          <ToggleRight className="w-4 h-4 inline mr-1" />
          {t('productDiscounts.toggleActive', 'Toggle Active')}
        </button>
        <button
          onClick={handleBulkDelete}
          disabled={bulkActionLoading}
          className="px-3 py-1.5 bg-red-100 text-red-800 rounded-lg text-sm font-medium hover:bg-red-200 disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4 inline mr-1" />
          {t('productDiscounts.deleteSelected', 'Delete Selected')}
        </button>
      </FloatingActionBar>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        icon="danger"
        confirmLabel={t('productDiscounts.delete', 'Delete')}
        confirmColor="red"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default ProductDiscounts;
